/*global Dingdocs*/

import { useEffect, useState, useCallback, useRef } from "react";
import { initView } from "dingtalk-docs-cool-app";
import {
  Button,
  Typography,
  Select,
  Table,
  message,
} from "dingtalk-design-desktop";
import "./style.css";
import { locales, Locales } from "./locales";

// === 调试开关 ===
const DEBUG = false;
const log = (label: string, ...args: any[]) => {
  if (DEBUG) {
    console.log(`[调试] ${label}:`, ...args);
  }
};

// 初始化钉钉鉴权（企业插件必须）
async function initConfigPermission(): Promise<boolean> {
  try {
    const currentUrl = window.location.href;
    log("当前页面URL用于鉴权:", currentUrl);

    const response = await fetch(
      `/api/configPermission?url=${encodeURIComponent(currentUrl)}`
    );
    if (!response.ok) {
      throw new Error(`请求鉴权配置失败: ${response.status}`);
    }

    const configData = await response.json();
    log("鉴权配置数据:", configData);

    await Dingdocs.base.host.configPermission(
      configData.agentId,
      configData.corpId,
      configData.timeStamp,
      configData.nonceStr,
      configData.signature,
      configData.jsApiList || ["DingdocsScript.base.readWriteAll"]
    );

    log("鉴权成功");
    return true;
  } catch (error) {
    console.error("鉴权失败:", error);
    return false;
  }
}

interface Sheet {
  id: string;
  name: string;
}

interface View {
  id: string;
  name: string;
  type: string;
}

interface SelectedRecord {
  recordId: string;
  title: string;
  loading?: boolean;
}

interface RecordDetail {
  id: string;
  title: string;
}

function App() {
  const [initialized, setInitialized] = useState(false);
  const [locale, setLocale] = useState<Locales>(locales["zh-CN"]);
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [selectedSheetId, setSelectedSheetId] = useState<string>("");
  const [views, setViews] = useState<View[]>([]);
  const [selectedViewId, setSelectedViewId] = useState<string>("");
  const [selectedRecords, setSelectedRecords] = useState<SelectedRecord[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [isSplitting, setIsSplitting] = useState(false);
  const [isUnsupportedSheet, setIsUnsupportedSheet] = useState(false);

  // 支持的数据表名称
  const SUPPORTED_SHEET_NAME = "询价预处理表";

  // 新增数据后滚动到底部
  useEffect(() => {
    if (selectedRecords.length > 0) {
      setTimeout(() => {
        const scrollBody = document.querySelector(
          ".table-wrapper .dtd-table-body",
        );
        if (scrollBody) {
          scrollBody.scrollTop = scrollBody.scrollHeight;
        }
      }, 50);
    }
  }, [selectedRecords.length]);

  // 使用 ref 存储最新值，避免 useEffect 依赖变化
  const selectedRecordsRef = useRef(selectedRecords);
  selectedRecordsRef.current = selectedRecords;

  // 防抖 ref：记录上次处理的 recordId 和时间
  const lastProcessedRef = useRef<{ recordId: string; time: number } | null>(
    null,
  );

  // 数据表切换防抖 ref：防止切换时弹出多个警告
  const lastSheetSwitchRef = useRef<{ sheetId: string; time: number } | null>(
    null,
  );

  // 加载数据表列表
  const loadSheets = useCallback(async () => {
    try {
      const sheetList = await Dingdocs.script.run("getAllSheets");
      const activeSheet = await Dingdocs.script.run("getActiveSheet");

      setSheets(sheetList);

      // 自动选择当前表
      if (activeSheet) {
        setSelectedSheetId(activeSheet.id);
        // 检查是否为支持的数据表
        if (activeSheet.name !== SUPPORTED_SHEET_NAME) {
          setIsUnsupportedSheet(true);
          message.warning(
            `${locale.unsupportedSheet.replace("{name}", activeSheet.name)}`,
          );
        } else {
          setIsUnsupportedSheet(false);
        }
        return activeSheet.id;
      } else if (sheetList.length > 0) {
        setSelectedSheetId(sheetList[0].id);
        return sheetList[0].id;
      }
      return null;
    } catch (error) {
      console.error("加载数据表失败:", error);
      return null;
    }
  }, []);

  // 加载视图列表
  const loadViews = useCallback(async (sheetId: string) => {
    try {
      const viewList = await Dingdocs.script.run("getSheetViews", sheetId);
      const activeView = await Dingdocs.script.run("getActiveView", sheetId);

      setViews(viewList);

      // 自动选择当前视图
      if (activeView) {
        setSelectedViewId(activeView.id);
      } else if (viewList.length > 0) {
        setSelectedViewId(viewList[0].id);
      }
    } catch (error) {
      console.error("加载视图失败:", error);
    }
  }, []);

  // 获取记录详情
  const fetchRecordDetail = useCallback(async (recordId: string) => {
    try {
      const result: RecordDetail = await Dingdocs.script.run(
        "getRecord",
        recordId,
      );
      return {
        recordId: recordId,
        title: result.title || "(无标题)",
      };
    } catch (error) {
      console.error("获取记录详情失败:", error);
      return {
        recordId: recordId,
        title: "(获取失败)",
      };
    }
  }, []);

  // 处理选中变化
  const handleSelectionChange = useCallback(
    (selection: any) => {
      const { recordId, sheetId } = selection;

      // 检测是否切换了数据表（带防抖）
      if (sheetId && sheetId !== selectedSheetId) {
        const now = Date.now();
        // 防抖：如果 500ms 内已经处理过同一个 sheetId，跳过
        if (
          lastSheetSwitchRef.current &&
          lastSheetSwitchRef.current.sheetId === sheetId &&
          now - lastSheetSwitchRef.current.time < 500
        ) {
          return;
        }
        lastSheetSwitchRef.current = { sheetId, time: now };

        // 更新当前数据表
        setSelectedSheetId(sheetId);
        // 清空选中列表和勾选状态
        setSelectedRecords([]);
        setCheckedKeys([]);
        // 切换数据表后重新加载视图
        loadViews(sheetId).catch((error) => {
          console.error("加载视图失败:", error);
        });

        // 检查新数据表是否支持
        const sheetName = sheets.find((s) => s.id === sheetId)?.name;
        if (sheetName && sheetName !== SUPPORTED_SHEET_NAME) {
          setIsUnsupportedSheet(true);
          message.warning(
            `${locale.unsupportedSheet.replace("{name}", sheetName)}`,
          );
        } else {
          setIsUnsupportedSheet(false);
        }
      }

      // 如果没有 recordId，不处理
      if (!recordId) {
        return;
      }

      // 防抖：如果 300ms 内已经处理过同一个 recordId，跳过
      const now = Date.now();
      if (
        lastProcessedRef.current &&
        lastProcessedRef.current.recordId === recordId &&
        now - lastProcessedRef.current.time < 300
      ) {
        log("防抖", `跳过重复触发: ${recordId}`);
        return;
      }
      lastProcessedRef.current = { recordId, time: now };

      // 使用 ref 获取最新值，避免闭包问题
      const currentRecords = selectedRecordsRef.current;

      // 检查是否已存在（包括 loading 中的项）
      const exists = currentRecords.some(
        (r) => r.recordId === recordId || r.recordId === `temp_${recordId}`,
      );
      if (exists) {
        // 移除
        setSelectedRecords((prev) =>
          prev.filter(
            (r) => r.recordId !== recordId && r.recordId !== `temp_${recordId}`,
          ),
        );
      } else {
        // 先添加加载中的项
        const tempId = `temp_${recordId}`;
        setSelectedRecords((prev) => [
          ...prev,
          {
            recordId: tempId,
            title: locale.loadingTitle,
            loading: true,
          },
        ]);

        // 异步获取真实数据
        fetchRecordDetail(recordId)
          .then((detail) => {
            // 用真实 recordId 替换临时项
            setSelectedRecords((prev) => {
              const idx = prev.findIndex((r) => r.recordId === tempId);
              if (idx !== -1) {
                const newRecords = [...prev];
                newRecords[idx] = { ...detail, loading: false };
                return newRecords;
              }
              return prev;
            });
          })
          .catch(() => {
            // 获取失败，移除加载项
            setSelectedRecords((prev) =>
              prev.filter((r) => r.recordId !== tempId),
            );
          });
      }
    },
    [selectedSheetId, sheets, fetchRecordDetail, loadViews],
  );

  // 初始化和事件监听（合并到同一个 useEffect）
  useEffect(() => {
    let offSelectionChanged: (() => void) | null = null;

    initView({
      onReady: async () => {
        try {
          // 获取当前语言设置
          const currentLocale = await Dingdocs.base.host.getLocale();
          setLocale(locales[currentLocale] || locales["zh-CN"]);
        } catch (e) {
          console.warn("Failed to get locale, using default zh-CN");
        }

        // ========== 鉴权相关 ==========
        const authSuccess = await initConfigPermission();
        if (!authSuccess) {
          console.error("鉴权失败，插件将持续显示加载状态");
          return;
        }
        log("鉴权初始化完成");
        // ========== 鉴权相关结束 ==========

        // 加载数据表列表
        const sheetId = await loadSheets();

        // 如果有选中的数据表，加载视图
        if (sheetId) {
          await loadViews(sheetId);
        }

        // 注册选区变化事件监听
        offSelectionChanged = Dingdocs.base.event.onSelectionChanged(handleSelectionChange);

        setInitialized(true);
      },
      onError: (e) => {
        console.error("UI initialization error:", e);
      },
    });

    // 清理函数：组件卸载时注销事件监听
    return () => {
      if (offSelectionChanged) {
        offSelectionChanged();
      }
    };
  }, [loadSheets, loadViews]);

  // 执行拆分的核心逻辑
  const executeSplit = async () => {
    log("按钮-selectedRecords", selectedRecords);
    log(
      "按钮-recordIds",
      selectedRecords.map((r) => r.recordId),
    );

    try {
      const recordIds = selectedRecords.map((r) => r.recordId);
      const result = await Dingdocs.script.run("splitRecords", recordIds);
      log("拆分成功", result);

      // 根据结果决定是否警告
      if (result.skipped) {
        message.warning(result.message);
      }

      // 清空选中列表
      setSelectedRecords([]);
      setCheckedKeys([]);
    } catch (error) {
      console.error("拆分失败:", error);
    }
  };

  // 处理按钮点击
  const handleButtonClick = useCallback(() => {
    // 有 loading 中的记录，需要等待
    if (selectedRecords.some((r) => r.loading)) {
      setIsSplitting(true);
      // 每 100ms 检查一次，直到所有记录加载完成
      const timer = setInterval(() => {
        if (!selectedRecordsRef.current.some((r) => r.loading)) {
          clearInterval(timer);
          executeSplit().then(() => setIsSplitting(false));
        }
      }, 100);
    } else {
      // 没有 loading 记录，直接执行
      executeSplit();
    }
  }, [selectedRecords]);

  const handleSheetChange = useCallback(
    async (value: string) => {
      console.log("Sheet changed to:", value);
      setSelectedSheetId(value);
      setSelectedRecords([]); // 清空选中列表
      setCheckedKeys([]); // 清空勾选状态

      // 切换数据表后，重新加载视图
      await loadViews(value);
    },
    [loadViews],
  );

  const handleViewChange = useCallback((value: string) => {
    console.log("View changed to:", value);
    setSelectedViewId(value);
  }, []);

  // 表格列定义
  const columns = [
    {
      title: locale.columnTitle,
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title: string, record: SelectedRecord) => {
        if (record.loading) {
          return (
            <Typography.Text type="secondary" style={{ fontStyle: "italic" }}>
              {title}
            </Typography.Text>
          );
        }
        return title;
      },
    },
  ];

  // 删除选中记录
  const deleteSelectedRecords = useCallback((ids: string[]) => {
    setSelectedRecords((prev) => prev.filter((r) => !ids.includes(r.recordId)));
    setCheckedKeys([]);
  }, []);

  if (!initialized) {
    return (
      <div className="page">
        <div className="loading">
          <Typography.Text>{locale.loading}</Typography.Text>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="header">
        <Typography.Title level={4}>{locale.title}</Typography.Title>
      </div>

      <div className="content">
        {/* 选中记录区域（包含选择器和表格） */}
        <div className="main-container">
          <div className="table-wrapper">
            {/* 数据表选择（只展示，不可更改） */}
            <div className="table-filter">
              <Typography.Text
                strong
                style={{ display: "block", marginBottom: 8 }}
              >
                {locale.currentSheet}
              </Typography.Text>
              <Typography.Text>
                {sheets.find((s) => s.id === selectedSheetId)?.name ||
                  locale.loading}
              </Typography.Text>
            </div>

            {/* 视图选择 */}
            <div className="table-filter">
              <Typography.Text
                strong
                style={{ display: "block", marginBottom: 8 }}
              >
                {locale.selectView}
              </Typography.Text>
              <Select
                value={selectedViewId}
                onChange={handleViewChange}
                style={{ width: "100%" }}
                placeholder={locale.loading}
              >
                {views.map((view) => (
                  <Select.Option key={view.id} value={view.id}>
                    {view.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* 引导文本 */}
            <div className="table-filter">
              <Typography.Text type="secondary">
                {locale.guideText}
              </Typography.Text>
            </div>

            {/* 表格头部 */}
            <div className="table-header">
              <Typography.Text strong>
                {locale.selectedCount} ({selectedRecords.length})
              </Typography.Text>
              <Button
                size="small"
                disabled={checkedKeys.length === 0}
                onClick={() => deleteSelectedRecords(checkedKeys)}
              >
                {locale.deleteSelected} ({checkedKeys.length})
              </Button>
            </div>

            {/* 表格 */}
            <Table
              dataSource={selectedRecords}
              columns={columns}
              rowKey="recordId"
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
              style={{ width: "100%" }}
              rowSelection={{
                selectedRowKeys: checkedKeys,
                onChange: (keys: React.Key[]) => {
                  setCheckedKeys(keys as string[]);
                },
              }}
            />
          </div>

          {/* 按钮移至底部 */}
          <div className="button-container">
            <Button
              type="primary"
              block
              disabled={selectedRecords.length === 0 || isUnsupportedSheet}
              loading={isSplitting}
              onClick={handleButtonClick}
            >
              {locale.buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
